import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import type { ReactNode } from "react";

import { useToast } from "@/ds";

import type { SimulacaoAtivaApi } from "../simulacao.gateway";
import { SimulacaoContext } from "./simulacao-context";
import type { SimulacaoContextValue } from "./simulacao-context";
import { SIMULACAO_INATIVA, simulacaoReducer } from "./simulacao.model";
import { chaveAlteracao } from "./simulacao.model";
import { contarAlteracoes } from "./simulacao.resumo";
import {
  TOAST_SIMULACAO_ATIVA_FALHOU,
  TOAST_SIMULACAO_ATIVA_FALHOU_DETALHE,
} from "./simulacao.textos";
import { converterItensCenario, instrucaoParaAlteracao } from "./simulacao.instrucao";
import {
  carregarSimulacaoAtivaInicial,
  limparSimulacaoSessao,
  sincronizarSimulacaoSessao,
} from "./simulacao-sessao";

/**
 * Guarda o Modo Simulação acima das perspectivas da Carga de Trabalho, para que o
 * cenário sobreviva à troca entre Profissionais, Fornecedor e Local.
 *
 * A simulação é persistida na API:
 * - Ao montar, faz GET /ativa. 200 hidrata a faixa; 204 deixa o modo desligado; falha avisa por toast.
 * - "Sair" esconde o banner na sessão, mas NÃO descarta a simulação.
 * - Recarregar a página reconstitui o estado via GET /ativa.
 */
export function SimulacaoProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(simulacaoReducer, SIMULACAO_INATIVA);
  const [persistida, setPeristida] = useState<SimulacaoAtivaApi | null>(null);
  const [idsExtras, setIdsExtras] = useState<ReadonlyMap<string, string>>(() => new Map());
  const [loading, setLoading] = useState(true);
  const abortRef = useRef<AbortController | null>(null);
  const toast = useToast();

  const aplicarSimulacaoAtivaNoEstado = useCallback((ativa: SimulacaoAtivaApi) => {
    setPeristida(ativa);
    setIdsExtras(new Map());
    const cenario = converterItensCenario(ativa.itens);
    dispatch({ type: "entrar" });
    dispatch({ type: "redefinir", alteracoes: cenario.alteracoes });
  }, []);

  // Hidratação inicial: GET /ativa ao montar a Carga de Trabalho.
  useEffect(() => {
    const controller = new AbortController();
    abortRef.current = controller;

    carregarSimulacaoAtivaInicial(controller.signal)
      .then((ativa) => {
        sincronizarSimulacaoSessao(ativa);
        if (ativa?.status === "ATIVA") {
          aplicarSimulacaoAtivaNoEstado(ativa);
        }
      })
      .catch(() => {
        // Efeito desmontado ou refeito (StrictMode): a requisição cancelada não é falha.
        if (controller.signal.aborted) return;
        toast.error(TOAST_SIMULACAO_ATIVA_FALHOU, TOAST_SIMULACAO_ATIVA_FALHOU_DETALHE);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [aplicarSimulacaoAtivaNoEstado, toast]);

  const hidratarSimulacaoAtiva = useCallback(
    (dados: SimulacaoAtivaApi) => {
      sincronizarSimulacaoSessao(dados);
      if (dados.status !== "ATIVA") {
        setPeristida(null);
        setIdsExtras(new Map());
        return;
      }
      aplicarSimulacaoAtivaNoEstado(dados);
    },
    [aplicarSimulacaoAtivaNoEstado],
  );

  const registrarInstrucao = useCallback((chave: string, itemId: string) => {
    setIdsExtras((current) => {
      const next = new Map(current);
      next.set(chave, itemId);
      return next;
    });
  }, []);

  /** Reconstrói o mapa chave → instrucaoId a partir dos itens da API e dos ids do PATCH. */
  const mapaChaveParaItemId = useMemo<ReadonlyMap<string, string>>(() => {
    const mapa = new Map(idsExtras);
    if (persistida) {
      for (const item of persistida.itens) {
        const alteracao = instrucaoParaAlteracao(item);
        if (alteracao) mapa.set(chaveAlteracao(alteracao), item.id);
      }
    }
    return mapa;
  }, [persistida, idsExtras]);

  const limparSimulacaoAtiva = useCallback(() => {
    limparSimulacaoSessao();
    setPeristida(null);
    setIdsExtras(new Map());
  }, []);

  const value = useMemo<SimulacaoContextValue>(
    () => ({
      state,
      cenario: state.ativo ? state.cenario : null,
      // O contador vem de qtdAlteracoes da API quando há simulação persistida,
      // evitando a contagem dupla do reducer local. Quando a reconsulta pós-PATCH
      // retorna null (ex.: falha de rede em testes), cai no contarAlteracoes local
      // como fallback para não zerar o contador na tela.
      alteracoes: persistida
        ? Math.max(persistida.qtdAlteracoes, state.ativo ? contarAlteracoes(state.cenario) : 0)
        : state.ativo
          ? contarAlteracoes(state.cenario)
          : 0,
      simulacaoId: persistida?.status === "ATIVA" ? persistida.id : null,
      versao: persistida?.status === "ATIVA" ? persistida.versao : null,
      divergenciasApi: persistida?.divergencias ?? [],
      loading,
      haSimulacaoAtiva: persistida?.status === "ATIVA",
      mapaChaveParaItemId,
      entrar: () => dispatch({ type: "entrar" }),
      sair: () => dispatch({ type: "sair" }),
      incluir: (alteracao) => dispatch({ type: "incluir", alteracao }),
      remover: (chave) => dispatch({ type: "remover", chave }),
      redefinir: (alteracoes) => dispatch({ type: "redefinir", alteracoes }),
      hidratarSimulacaoAtiva,
      registrarInstrucao,
      limparSimulacaoAtiva,
    }),
    [
      state,
      persistida,
      loading,
      hidratarSimulacaoAtiva,
      registrarInstrucao,
      limparSimulacaoAtiva,
      mapaChaveParaItemId,
    ],
  );

  return <SimulacaoContext.Provider value={value}>{children}</SimulacaoContext.Provider>;
}
